import { HttpClient, HttpErrorResponse, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserInput } from '../models/userInput';
import { environment } from '../../environments/environment';
import { UnitsSettings } from '../models/unitsSettings';
import { UserPreferences } from '../models/user-preferences';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { PowerUnits } from '../models/power-units';
import { TorqueUnits } from '../models/torque-units';
import { SpeedUnits } from '../models/speed-units';
import { WeightUnits } from '../models/weight-units';
import { LengthUnits } from '../models/length-units';
import { CalculatedSettings, Drivetrain, DynoPoint } from '../models/calculatedSettings';
import { CarTrackSettings } from '../models/car-track-settings';
import { DrivetrainSettings } from '../models/drivetrain-settings';
import { UserSubscription } from '../models/subscription';
import { SuspensionSettings } from '../models/suspension-settings';
import { Suspension } from '../models/Suspension';
import { Vehicle } from '../models/vehicle';
import { PagedDataResult } from '../models/paged-data-result';
import { SavedSettings } from '../models/saved-settings';
import { AppSubscription } from './auth.service';
import { SubscriptionLevels } from '../models/subscription-levels';
import { ErrorHandlerService } from './error-handler.service';
import { AppNotification } from '../models/notification';
import { IUnit } from '../interfaces/unit';
import { AnalyzedImageData } from '../models/analyzed-image-data';
import { UserSearch } from '../models/user-search';
import { UserCount } from '../models/user-count';
import { User } from '../models/user';


@Injectable({
  providedIn: 'root',

})
export class ProtuneApiService {
  baseUrl: string = environment.protuneApiUrl;
  vehicles: Vehicle[];

  constructor(private http: HttpClient, private errorHandler: ErrorHandlerService) { }

  testError() {
    return this.http.get<any>(`${this.baseUrl}/settings`)
      .pipe(catchError((err: any, caught) => {
        return this.errorHandler.handleError("Called test error endpoint.", err.error, err.request, true)
      }));
  }

  getSettings(drivetrainSettings: DrivetrainSettings, carTrackSettings: CarTrackSettings) {
    let data = {
      ...drivetrainSettings,
      ...carTrackSettings
    };

    return this.http.post<Drivetrain>(`${this.baseUrl}/settings/drivetrain`, data)
      .pipe(
        map((drivetrain: Drivetrain): Drivetrain => {
          if (drivetrain) {
            for (let key of Object.keys(drivetrain.gears)) {
              let gear = drivetrain.gears[parseInt(key)];
              //if (drivetrainSettings.engineData != null) {
                for (let ed of drivetrainSettings.engineData) {
                  let wheelCount = drivetrain.driveWheelCount;
                  let finalGear = drivetrain.finalGear;
                  let torqueNm = ed.torque * 1.3558;
                  let speed = (ed.rpm * carTrackSettings.tireSize * 0.002975) / (finalGear * gear.gearRatio);
                  let tireRadius = (carTrackSettings.tireSize / 2) * 0.0254;
                  let wheelTorque = (torqueNm * gear.gearRatio * finalGear) / wheelCount / tireRadius;

                  let dp: DynoPoint = {
                    rpm: ed.rpm,
                    torqueFtLbs: ed.torque,
                    torqueNm: torqueNm,
                    hp: ed.power,
                    speed: speed,
                    wheelTorque: wheelTorque
                  };

                  gear.dynoPoints.push(dp);
                }
              //}
            }
          }

          return drivetrain
        })
      )
  }

  getSuspensionSettings(suspensionSettings: SuspensionSettings, carTrackSettings: CarTrackSettings, vehicle: Vehicle) {
    let data = {
      ...suspensionSettings,
      ...carTrackSettings,
      ...vehicle,
      vehicleId: vehicle.id
    };

    return this.http.post<Suspension>(`${this.baseUrl}/settings/suspension`, data);
  }

  getParametersFromImage(torqueUnits: IUnit, file1: File = null, file2: File = null, file3: File = null) {
    let formData = new FormData();
    if (file1 != null) {
      formData.append('file1', file1, file1.name)
    }

    if (file2 != null) {
      formData.append('file2', file2, file2.name)
    }

    if (file3 != null) {
      formData.append('file3', file3, file3.name)
    }
    const options = { params: new HttpParams().set('torqueUnits', torqueUnits.id) };

    return this.http
      .post<AnalyzedImageData>(`${this.baseUrl}/settings/analyzeimage`, formData, options);
  }

  getManufacturers() {
    return this.http.get(`${this.baseUrl}/Vehicles/manufacturers`);
  }

  getVehiclesByManufacturer(manufacturer: string) {
    encodeURIComponent(manufacturer)
    return this.http
      .get<PagedDataResult<Vehicle>>(`${this.baseUrl}/vehicles/search`, {
        params: {
          manufacturer,
          pageSize: 100,
          page: 1
        }
      });
  }

  getVehiclesBySearchTerm(search: string) {
    return this.http
      .get<PagedDataResult<Vehicle>>(`${this.baseUrl}/vehicles/search`, {
        params: {
          search,
          pageSize: 1000,
          page: 1
        }
      });
  }

  getAllVehicles() {
    if (this.vehicles && this.vehicles.length > 0) {
      return of(this.vehicles);
    }
    else {
      return this.http
        .get<Vehicle[]>(`${this.baseUrl}/vehicles/list`)
        .pipe(
          tap((vehicles) => {
            this.vehicles = vehicles;
          })
        );
    }
  }

  saveUserPreferences(preferences: UserPreferences) {
    let model = {
      powerUnits: preferences.powerUnits.id,
      torqueUnits: preferences.torqueUnits.id,
      speedUnits: preferences.speedUnits.id,
      weightUnits: preferences.weightUnits.id,
      lengthUnits: preferences.lengthUnits.id
    };

    return this.http.post(`${this.baseUrl}/user/preferences`, model);
  }

  getUserPreferences() {
    return this.http.get(`${this.baseUrl}/user/preferences`)
      .pipe(map((p: any) => {
        let preferences: UserPreferences = {
          powerUnits: PowerUnits.getById(p.powerUnits),
          torqueUnits: TorqueUnits.getById(p.torqueUnits),
          speedUnits: SpeedUnits.getById(p.speedUnits),
          weightUnits: WeightUnits.getById(p.weightUnits),
          lengthUnits: LengthUnits.getById(p.lengthUnits),
          mode: 'lite'
        };
        return preferences;
      }));
  }

  getPaymentLink(products: string[]) {
    const options = { params: new HttpParams().set('products', products.join(','))};
    return this.http.get(`${this.baseUrl}/payment/link`, options)
  }

  getBillingPortalLink() {
    return this.http.get(`${this.baseUrl}/payment/billingPortal`);
  }

  updateUserSubscriptionWithSessionId(sessionId: string) {
    const options = { params: new HttpParams().set('sessionId', sessionId) };
    return this.http.get<AppSubscription>(`${this.baseUrl}/user/updatesubscription`, options);
   
  }

  getSubscriptionInfo() {
    return this.http.get<AppSubscription>(`${this.baseUrl}/user/subscription`);
  }

  cancelSubscription() {
    return this.http.get(`${this.baseUrl}/user/cancelsubscription`);
  }



  saveSettings(savedSettingsId: number, name: string, carTrackSettings: CarTrackSettings, suspensionSettings: SuspensionSettings, drivetrainSettings: DrivetrainSettings) {
    return this.http.post<number>(`${this.baseUrl}/settings/savedsettings`, {
      savedSettingsId: savedSettingsId,
      name: name,
      vehicleId: carTrackSettings.vehicle.id,
      carTrackSettings: JSON.stringify(carTrackSettings),
      suspensionSettings: JSON.stringify(suspensionSettings),
      drivetrainSettings: JSON.stringify(drivetrainSettings)
    })
  }

  getSavedSettings(page: number | string | null= '', pageSize: number | string | null = '', manufacturer: string | null = '', search: string | null = '') {
    const options = {
      params: new HttpParams()
        .set('page', page)
        .set('pageSize', pageSize)
        .set('manufacturer', manufacturer)
        .set('search', search)
    };

    return this.http.get<PagedDataResult<SavedSettings>>(`${this.baseUrl}/settings/savedsettings`, options);
  }

  getSavedSettingsWithUrl(url: string) {
    return this.http.get<PagedDataResult<SavedSettings>>(url);
  }

  deleteSavedSettings(savedSettingsId: number) {
    return this.http.delete(`${this.baseUrl}/settings/savedsettings/${savedSettingsId}`);
  }

  getAppNotifications() {
    return this.http.get<AppNotification[]>(`${this.baseUrl}/notifications`);
  }

  acknowledgeAppNotifications(notificationIds: number[]) {
    return this.http.post(`${this.baseUrl}/notifications/ack`, notificationIds);
  }


  getUsers(userSearch: UserSearch) {

    const options = {
      params: new HttpParams()
        .set('page', userSearch.page)
        .set('pageSize', userSearch.pageSize)
        .set('sortDirection', userSearch.sortDirection)
        .set('sortColumn', userSearch.sortColumn)
        .set('subscriptionLevels', userSearch.subscriptionLevels)
        .set('search', userSearch.search)
        .set('createdStartDate', userSearch.createdStartDate?.toISOString() ?? '')
        .set('createdEndDate', userSearch.createdEndDate?.toISOString() ?? '')
        .set('isEnabled', userSearch.isEnabled)
    };

    return this.http.get<PagedDataResult<any>>(`${this.baseUrl}/administration/users`, options);
  }

  exportUsers(userSearch: UserSearch) {
    const options = {
      params: new HttpParams()
        .set('sortDirection', userSearch.sortDirection)
        .set('sortColumn', userSearch.sortColumn)
        .set('subscriptionLevels', userSearch.subscriptionLevels)
        .set('search', userSearch.search)
        .set('createdStartDate', userSearch.createdStartDate?.toISOString() ?? '')
        .set('createdEndDate', userSearch.createdEndDate?.toISOString() ?? '')
        .set('isEnabled', userSearch.isEnabled),
      responseType: 'blob' as const
    };

    return this.http.get(`${this.baseUrl}/administration/users/export`, options);
  }

  getUser(id: string) {
    return this.http.get<User>(`${this.baseUrl}/administration/users/${id}`)
  }
  getUserCount(userCount: UserCount) {
    const options = {
      params: new HttpParams()
        .set('subscriptionLevels', userCount.subscriptionLevels)
        .set('createdStartDate', userCount.createdStartDate?.toISOString() ?? '')
        .set('createdEndDate', userCount.createdEndDate?.toISOString() ?? '')
        .set('isEnabled', userCount.isEnabled)
    };

    return this.http.get<number>(`${this.baseUrl}/administration/users/count`, options);
  }

  updateUser(user: User) {
    return this.http.post<User>(`${this.baseUrl}/administration/users/${user.id}`, user);
  }

  getProducts() {
    return this.http.get<any>('/assets/config.json', { headers: { skip: 'true' } })
      .pipe(map((p: any) => {
        return p.products;
      }));
  }

  getSavedSettingsByUser(userId: string, page: number | string | null = 1, pageSize: number | string | null = 15, manufacturer: string | null = '', search: string | null = '') {
    const options = {
      params: new HttpParams()
        .set('page', page)
        .set('pageSize', pageSize)
        .set('manufacturer', manufacturer)
        .set('search', search)
    };

    return this.http.get<PagedDataResult<SavedSettings>>(`${this.baseUrl}/administration/users/${userId}/savedsettings`, options);
  }
}
